// File: src/EnglishTool/readingPool.js

/**
 * getReadingPool(grade)
 * Returns an array of reading comprehension objects for the given grade level.
 * Each object has:
 *    { statement: "Some text here", mainIdea: "The main idea or summary" }
 */
export function getReadingPool(grade) {
    const data = {
      1: [
        {
          statement: "The sun gives us light and warmth.",
          mainIdea: "The sun helps us by giving light and warmth."
        },
        {
          statement: "A bird uses its wings to fly high.",
          mainIdea: "Birds fly with their wings."
        },
        {
          statement: "Winter is cold, and it sometimes snows.",
          mainIdea: "Winter is a cold season with snow."
        },
        {
          statement: "The cat sleeps on a cozy rug.",
          mainIdea: "The cat rests on a comfortable rug."
        },
        {
          statement: "We drink water when we feel thirsty.",
          mainIdea: "Water helps us when we're thirsty."
        }
      ],
      2: [
        {
          statement: "My mom bakes bread that smells delicious.",
          mainIdea: "Mom bakes yummy bread."
        },
        {
          statement: "Fish can breathe underwater using gills.",
          mainIdea: "Fish use gills underwater."
        },
        {
          statement: "Dogs wag their tails when they are happy.",
          mainIdea: "Dogs show happiness by wagging tails."
        },
        {
          statement: "Plants need sunlight and water to grow.",
          mainIdea: "Plants require sunlight and water."
        },
        {
          statement: "He practices the piano every day to get better.",
          mainIdea: "Daily practice helps improve piano skills."
        }
      ],
      3: [
        {
          statement: "Bees gather nectar from flowers and make honey.",
          mainIdea: "Bees use flower nectar to create honey."
        },
        {
          statement: "The students enjoyed a field trip to the museum.",
          mainIdea: "Students had fun visiting the museum."
        },
        {
          statement: "She recycles paper and plastic to help the environment.",
          mainIdea: "Recycling protects the environment."
        },
        {
          statement: "Clouds bring rain that waters the plants.",
          mainIdea: "Rain from clouds helps plants grow."
        },
        {
          statement: "Reading a variety of books increases our knowledge.",
          mainIdea: "Different books can help us learn more."
        }
      ],
      4: [
        {
          statement: "Farmers plant seeds in spring and harvest crops in fall.",
          mainIdea: "Farmers follow a cycle of planting and harvesting."
        },
        {
          statement: "Teamwork is essential when building a complex project.",
          mainIdea: "Working together is important to complete a project."
        },
        {
          statement: "Some animals migrate long distances to find food.",
          mainIdea: "Animals move far away to get food."
        },
        {
          statement: "Volcanoes erupt when pressure builds up under the earth's surface.",
          mainIdea: "Volcano eruptions happen due to underground pressure."
        },
        {
          statement: "He practiced his speech to feel confident before presenting.",
          mainIdea: "Preparation boosts confidence in public speaking."
        }
      ],
      5: [
        {
          statement: "Green energy sources reduce pollution and conserve resources.",
          mainIdea: "Using green energy helps the environment."
        },
        {
          statement: "The class performed a play to raise funds for charity.",
          mainIdea: "They held a play to collect money for a good cause."
        },
        {
          statement: "She organized a study group to share notes and ideas.",
          mainIdea: "A study group helps people learn together."
        },
        {
          statement: "Scientists observe animal behavior to understand ecosystems better.",
          mainIdea: "Watching animals teaches us about their environment."
        },
        {
          statement: "Exercise strengthens muscles and improves health.",
          mainIdea: "Physical activity benefits our bodies."
        }
      ],
      6: [
        {
          statement: "He started a garden to learn about growing vegetables.",
          mainIdea: "Gardening is an educational way to grow food."
        },
        {
          statement: "The invention of the printing press revolutionized book production.",
          mainIdea: "Printing press made books more widely available."
        },
        {
          statement: "She challenged herself to read one new book every week.",
          mainIdea: "Reading regularly increases knowledge and skills."
        },
        {
          statement: "Regularly cleaning up trash keeps our neighborhoods safe and tidy.",
          mainIdea: "Community cleanliness improves living conditions."
        },
        {
          statement: "Leaders often inspire people to work toward a shared goal.",
          mainIdea: "Good leadership unites people for a common purpose."
        }
      ],
      7: [
        {
          statement: "The school plans to implement a recycling program to reduce waste.",
          mainIdea: "Recycling at school will help reduce waste."
        },
        {
          statement: "He discovered that consistent practice enhances musical performance.",
          mainIdea: "Steady practice improves music skills."
        },
        {
          statement: "Volunteers help rebuild homes after natural disasters strike.",
          mainIdea: "Volunteers assist people affected by disasters."
        },
        {
          statement: "The expansion of trade routes allowed for cultural exchange.",
          mainIdea: "Growing trade routes fostered sharing of cultures."
        },
        {
          statement: "Team sports often teach cooperation and strategy.",
          mainIdea: "Playing sports can develop teamwork skills."
        }
      ],
      8: [
        {
          statement: "Civic engagement empowers citizens to shape their communities.",
          mainIdea: "Being involved helps people improve their community."
        },
        {
          statement: "She found that exploring various art forms broadened her creativity.",
          mainIdea: "Trying different arts can increase creative thinking."
        },
        {
          statement: "Technological advances can solve environmental problems if used responsibly.",
          mainIdea: "Responsible technology helps solve ecological issues."
        },
        {
          statement: "He studied the effects of pollution on local wildlife habitats.",
          mainIdea: "Pollution negatively affects animal environments."
        },
        {
          statement: "Historical landmarks remind us of significant past events.",
          mainIdea: "Landmarks help us remember our history."
        }
      ],
      9: [
        {
          statement: "Renewable energy investments reduce our dependence on fossil fuels.",
          mainIdea: "Using renewables lowers reliance on nonrenewable resources."
        },
        {
          statement: "She researched how social media shapes modern communication trends.",
          mainIdea: "Social media has a big influence on communication today."
        },
        {
          statement: "Participating in debates helps students develop critical thinking skills.",
          mainIdea: "Debates encourage deeper reasoning abilities."
        },
        {
          statement: "International collaboration can lead to significant scientific breakthroughs.",
          mainIdea: "Working together globally advances scientific discoveries."
        },
        {
          statement: "Preserving biodiversity maintains balance in ecosystems.",
          mainIdea: "Protecting varied life forms keeps ecosystems stable."
        }
      ],
      10: [
        {
          statement: "Economic policies can have complex effects on global markets.",
          mainIdea: "Policies influence markets worldwide in complicated ways."
        },
        {
          statement: "She examined the philosophical concepts behind personal identity.",
          mainIdea: "She explored philosophical views of who we are."
        },
        {
          statement: "Climate models predict changes that could impact farming worldwide.",
          mainIdea: "Environmental shifts may affect agriculture on a global scale."
        },
        {
          statement: "Continuous education helps professionals adapt to new technologies.",
          mainIdea: "Lifelong learning aids in keeping up with technology."
        },
        {
          statement: "A diverse workplace often drives innovation and creativity.",
          mainIdea: "Different perspectives in a team boost new ideas."
        }
      ],
      11: [
        {
          statement: "Her research focused on how language barriers affect cross-cultural relationships.",
          mainIdea: "Communication gaps can hinder connections between cultures."
        },
        {
          statement: "Astrophysicists study the origins and structure of the universe.",
          mainIdea: "Scientists analyze how the cosmos began and is organized."
        },
        {
          statement: "Global health initiatives require cooperation among many nations.",
          mainIdea: "Working together internationally improves worldwide health."
        },
        {
          statement: "Democratic systems rely on informed citizens to make effective decisions.",
          mainIdea: "Voters need knowledge for democracy to function well."
        },
        {
          statement: "He explored existential themes in 20th-century literature.",
          mainIdea: "Literature from that era addresses questions of human existence."
        }
      ],
      12: [
        {
          statement: "Quantum computing has the potential to revolutionize data processing.",
          mainIdea: "New computing technology might vastly change data handling."
        },
        {
          statement: "Advanced genetic editing techniques spark ethical debates worldwide.",
          mainIdea: "Powerful DNA editing raises moral questions globally."
        },
        {
          statement: "Sustainability efforts aim to protect resources for future generations.",
          mainIdea: "Conservation focuses on preserving resources for the future."
        },
        {
          statement: "Global political alliances often shape international policies.",
          mainIdea: "Nations working together influence worldwide decisions."
        },
        {
          statement: "Philosophical inquiries into consciousness challenge our understanding of reality.",
          mainIdea: "Questions about the mind push us to reconsider what is real."
        }
      ]
    };
  
    // Return the array for the given grade, or an empty array if none is found
    return data[grade] || [];
  }
  